import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import { findDateRange } from '../../../pages/Calendar/Helpers/functions';
import API from '../../API/Main/API';
import {
	AdminCreateEventProperties,
	CalendarEventItem,
	CopyEventsProperties,
	CreateDiscretionaryHoursProperties,
	CreateEventProperties,
	DeleteEventsProperties,
	DeleteUpcomingEventsProperties,
	DeleteUserEventProperties,
	EventDataProperties,
	GetEventsProperties,
	RecurringScheduleSessionProperties,
	isDiscretionaryEvent,
	isRecurringScheduleSession,
} from '../../API/ClientAPIHelpers/calendarProperties';
import { CancellationStatus, ScheduleCancellationViewModel } from '../../API/ClientAPIHelpers/scheduleCancellation';
import { ErrorProperties } from '../../API/identityAPIProperties';

interface CalendarStateProperties {
	error: ErrorProperties;
	event: EventDataProperties;
	events: CalendarEventItem[] | null;
	isCreating: boolean;
	isDeleting: boolean;
	loading: boolean;
	activeGetEventsRequestId: string | null;
}

const initialEventState: EventDataProperties = {
	id: '',
	isCompleted: false,
	date: '',
	startTime: '',
	endTime: '',
	title: '',
	type: '',
	sessionType: {
		id: 0,
		name: '',
		type: 1,
		allowedRoleSections: [],
		funderId: null,
		requiresSoapNote: true,
	},
	user: {
		id: '',
		fullName: '',
		firstName: '',
		lastName: '',
		email: '',
		role: {
			id: '',
			name: '',
			section: {
				id: 0,
				name: '',
			},
		},
	},
	client: {
		id: '',
		dateOfBirthday: '',
		email: '',
		fullName: '',
		gender: '',
		defaultBcba: {
			id: '',
			fullName: '',
			firstName: '',
			lastName: '',
			email: '',
			role: {
				id: '',
				name: '',
				section: {
					id: 0,
					name: '',
				},
			},
		},
	},
	sessionId: '',
	reportStatus: null,
	reportId: null,
	isSessionFlagged: false,
};

const InitialCalendarState: CalendarStateProperties = {
	isCreating: false,
	isDeleting: false,
	loading: false,
	activeGetEventsRequestId: null,
	events: null,
	event: initialEventState,
	error: {
		status: 0,
		title: '',
	},
};

const RECURRING_FREQUENCY_WEEKLY = 0;
const RECURRING_FREQUENCY_MONTHLY = 1;
const RECURRING_FREQUENCY_DAILY = 2;

const emptyRole = {
	id: '',
	name: '',
	section: {
		id: 0,
		name: '',
	},
};

const toDateKey = (value: string) => dayjs(value).format('YYYY-MM-DD');

const minutesToTime = (minutes: number) =>
	`${String(Math.floor(minutes / 60)).padStart(2, '0')}:${String(minutes % 60).padStart(2, '0')}:00`;

// Reads the literal wall-clock HH:mm from the ISO string so dedupe doesn't drift with the viewer's
// timezone (dayjs(value).hour() would re-interpret the instant in the local zone).
export const timeToMinutes = (value: string) => {
	const match = /T(\d{2}):(\d{2})/.exec(value);

	if (match) {
		return parseInt(match[1], 10) * 60 + parseInt(match[2], 10);
	}

	const parsed = dayjs(value);

	return parsed.hour() * 60 + parsed.minute();
};

// Per LG-1297 / 6/2 decision: a scheduled (ghost) entry transitions into a real session once the
// provider starts it, and the actual clock-in time may differ from the planned window by up to an
// hour. So a ghost is suppressed by a same provider/client/session-type real session on the same
// day whose time window overlaps the scheduled window OR starts within this tolerance of it.
// This avoids duplicate blocks for a drifted clock-in WITHOUT hiding a genuinely separate same-day
// session of the same type (e.g. a morning + afternoon schedule). Each real session can be matched
// to at most one ghost.
export const REAL_SESSION_MATCH_TOLERANCE_MINUTES = 120;

const calendarEventKey = (event: EventDataProperties) =>
	`${event.user.id}|${event.client.id}|${event.sessionType.id}|${toDateKey(event.date)}`;

const recurringScheduleSessionKey = (recurringScheduleSession: RecurringScheduleSessionProperties, date: dayjs.Dayjs) =>
	`${recurringScheduleSession.providerId}|${recurringScheduleSession.clientId}|${recurringScheduleSession.sessionTypeId}|${date.format('YYYY-MM-DD')}`;

interface RealSessionWindow {
	endMinutes: number;
	eventIndex?: number;
	recurringScheduleTimeSlotId?: string | null;
	startMinutes: number;
	used: boolean;
}

// Finds an unconsumed real session that plausibly fulfils the scheduled (ghost) window: prefer an
// overlapping window, otherwise the closest start within tolerance. Returns -1 if none qualify.
export const findMatchingRealSessionIndex = (candidates: RealSessionWindow[], ghostStart: number, ghostEnd: number, recurringScheduleTimeSlotId?: string | null) => {
	if (recurringScheduleTimeSlotId) {
		const exactIndex = candidates.findIndex((candidate) => !candidate.used && candidate.recurringScheduleTimeSlotId === recurringScheduleTimeSlotId);
		if (exactIndex !== -1) return exactIndex;
	}

	let bestIndex = -1;
	let bestStartDiff = Number.POSITIVE_INFINITY;
	let bestIsOverlap = false;

	candidates.forEach((candidate, index) => {
		if (candidate.used) return;
		if (recurringScheduleTimeSlotId && candidate.recurringScheduleTimeSlotId) return;

		const overlap = candidate.startMinutes < ghostEnd && ghostStart < candidate.endMinutes;
		const startDiff = Math.abs(candidate.startMinutes - ghostStart);

		if (!overlap && startDiff > REAL_SESSION_MATCH_TOLERANCE_MINUTES) return;

		const better = (overlap && !bestIsOverlap) || (overlap === bestIsOverlap && startDiff < bestStartDiff);

		if (better) {
			bestIndex = index;
			bestStartDiff = startDiff;
			bestIsOverlap = overlap;
		}
	});

	return bestIndex;
};

const startOfWeek = (date: dayjs.Dayjs, weekStartsOn: number) => {
	const diff = (date.day() - weekStartsOn + 7) % 7;

	return date.startOf('day').subtract(diff, 'day');
};

export const shouldCreateRecurringScheduleSessionForDate = (recurringScheduleSession: RecurringScheduleSessionProperties, date: dayjs.Dayjs) => {
	const start = dayjs(recurringScheduleSession.startDate).startOf('day');
	const end = recurringScheduleSession.endDate ? dayjs(recurringScheduleSession.endDate).startOf('day') : null;

	if (date.isBefore(start, 'day')) return false;
	if (end && date.isAfter(end, 'day')) return false;

	const step = Math.max(recurringScheduleSession.step, 1);

	if (recurringScheduleSession.frequency === RECURRING_FREQUENCY_WEEKLY) {
		const weekDistance = startOfWeek(date, recurringScheduleSession.weekStartsOn).diff(startOfWeek(start, recurringScheduleSession.weekStartsOn), 'week');

		return recurringScheduleSession.daysOfWeek.includes(date.day()) && weekDistance % step === 0;
	}

	if (recurringScheduleSession.frequency === RECURRING_FREQUENCY_DAILY) {
		return date.diff(start, 'day') % step === 0;
	}

	if (recurringScheduleSession.frequency === RECURRING_FREQUENCY_MONTHLY) {
		return date.date() === start.date() && date.diff(start, 'month') % step === 0;
	}

	return false;
};

const buildRecurringScheduleSessionEvent = (recurringScheduleSession: RecurringScheduleSessionProperties, date: dayjs.Dayjs): EventDataProperties => {
	const dateKey = date.format('YYYY-MM-DD');

	return {
		id: `${recurringScheduleSession.id}-${recurringScheduleSession.recurringScheduleTimeSlotId ?? 'legacy'}-${dateKey}`,
		recurringScheduleId: recurringScheduleSession.id,
		recurringScheduleTimeSlotId: recurringScheduleSession.recurringScheduleTimeSlotId,
		isRecurringScheduleSession: true,
		isCompleted: false,
		isSessionFlagged: false,
		date: dateKey,
		// Wall-clock times without offset — dayjs() parses as local, matching how
		// the backend serializes real calendar events (no timezone suffix).
		startTime: `${dateKey}T${minutesToTime(recurringScheduleSession.startTimeMinutes)}`,
		endTime: `${dateKey}T${minutesToTime(recurringScheduleSession.endTimeMinutes)}`,
		title: recurringScheduleSession.clientName,
		type: 'RecurringSchedule',
		sessionId: '',
		reportId: null,
		reportStatus: null,
		sessionType: {
			id: recurringScheduleSession.sessionTypeId,
			name: recurringScheduleSession.sessionTypeName,
			type: recurringScheduleSession.sessionType,
			allowedRoleSections: [],
			funderId: null,
			requiresSoapNote: true,
		},
		user: {
			id: recurringScheduleSession.providerId,
			fullName: recurringScheduleSession.providerName,
			firstName: '',
			lastName: '',
			email: '',
			role: emptyRole,
		},
		client: {
			id: recurringScheduleSession.clientId,
			fullName: recurringScheduleSession.clientName,
			timeZoneId: recurringScheduleSession.clientTimeZoneId,
			dateOfBirthday: '',
			email: '',
			gender: '',
			isElc: recurringScheduleSession.isElc,
			defaultBcba: {
				id: '',
				fullName: '',
				firstName: '',
				lastName: '',
				email: '',
				role: emptyRole,
			},
		},
	};
};

interface IndexedCancellation {
	endDate: string;
	groupId: string | null;
	id: string;
	notes: string | null;
	reasonName: string | null;
	recurringScheduleTimeSlotId: string | null;
	startDate: string;
	status: CancellationStatus;
}

interface CancellationLookup {
	bySchedule: Map<string, IndexedCancellation[]>;
	extendedByProvider: Map<string, ScheduleCancellationViewModel[]>;
}

const buildCancellationLookup = (cancellations: ScheduleCancellationViewModel[]): CancellationLookup => {
	const bySchedule = new Map<string, IndexedCancellation[]>();
	const extendedByProvider = new Map<string, ScheduleCancellationViewModel[]>();
	cancellations.forEach((cancellation) => {
		if (!cancellation.recurringScheduleId) {
			const entries = extendedByProvider.get(cancellation.providerId) ?? [];
			entries.push(cancellation);
			extendedByProvider.set(cancellation.providerId, entries);
			return;
		}
		const entries = bySchedule.get(cancellation.recurringScheduleId) ?? [];
		entries.push({
			endDate: cancellation.endDate,
			groupId: cancellation.groupId,
			id: cancellation.id,
			notes: cancellation.notes,
			reasonName: cancellation.reasonName,
			recurringScheduleTimeSlotId: cancellation.recurringScheduleTimeSlotId ?? null,
			startDate: cancellation.startDate,
			status: cancellation.status,
		});
		bySchedule.set(cancellation.recurringScheduleId, entries);
	});
	return { bySchedule, extendedByProvider };
};

const applyCancellationToOccurrence = (event: EventDataProperties, lookup: CancellationLookup): EventDataProperties => {
	let isCancelled = false;
	let isCancelledApproved = false;
	let hasPendingCancellation = false;
	let cancellationReasonName: string | null = '';
	let cancellationNotes: string | null = null;
	let cancellationId: string | null = null;
	let cancellationGroupId: string | null = null;
	const scheduleCancellations = event.recurringScheduleId ? lookup.bySchedule.get(event.recurringScheduleId) : undefined;

	for (const cancellation of scheduleCancellations ?? []) {
		if (cancellation.recurringScheduleTimeSlotId && cancellation.recurringScheduleTimeSlotId !== event.recurringScheduleTimeSlotId) continue;
		if (!isDateWithinCancellationRange(event.date, cancellation.startDate, cancellation.endDate)) continue;
		if (cancellation.status === CancellationStatus.Applied || cancellation.status === CancellationStatus.Approved) {
			isCancelled = true;
			isCancelledApproved = cancellation.status === CancellationStatus.Approved;
			cancellationReasonName = cancellation.reasonName;
			cancellationNotes = cancellation.notes;
			cancellationId = cancellation.id;
			cancellationGroupId = cancellation.groupId;
			break;
		}
		if (cancellation.status === CancellationStatus.Pending) hasPendingCancellation = true;
	}

	if (!isCancelled) {
		for (const cancellation of lookup.extendedByProvider.get(event.user.id) ?? []) {
			if (cancellation.clientId && cancellation.clientId !== event.client.id) continue;
			if (!isDateWithinCancellationRange(event.date, cancellation.startDate, cancellation.endDate)) continue;
			if (cancellation.status === CancellationStatus.Applied || cancellation.status === CancellationStatus.Approved) {
				isCancelled = true;
				isCancelledApproved = cancellation.status === CancellationStatus.Approved;
				cancellationReasonName = cancellation.reasonName;
				cancellationNotes = cancellation.notes;
				cancellationId = cancellation.id;
				cancellationGroupId = cancellation.groupId;
				break;
			}
			if (cancellation.status === CancellationStatus.Pending) hasPendingCancellation = true;
		}
	}

	if (isCancelled) return { ...event, isCancelled, isCancelledApproved, cancellationReasonName, cancellationNotes, cancellationId, cancellationGroupId };
	if (hasPendingCancellation) return { ...event, hasPendingCancellation };
	return event;
};

const transferOccurrenceState = (event: CalendarEventItem, occurrence: EventDataProperties): CalendarEventItem => {
	if (isDiscretionaryEvent(event)) return event;
	if (!occurrence.isCancelled && !occurrence.hasPendingCancellation) return event;
	return {
		...event,
		recurringScheduleTimeSlotId: occurrence.recurringScheduleTimeSlotId,
		isCancelled: occurrence.isCancelled,
		isCancelledApproved: occurrence.isCancelledApproved,
		cancellationReasonName: occurrence.cancellationReasonName,
		cancellationNotes: occurrence.cancellationNotes,
		cancellationId: occurrence.cancellationId,
		cancellationGroupId: occurrence.cancellationGroupId,
		hasPendingCancellation: occurrence.hasPendingCancellation,
	};
};

export const expandRecurringScheduleSessions = (items: unknown[], startDate: string | null, endDate: string | null, cancellations: ScheduleCancellationViewModel[] = []): CalendarEventItem[] => {
	const rangeStart = dayjs(startDate);
	const rangeEnd = dayjs(endDate);
	const calendarEvents = items.filter((item): item is CalendarEventItem => !isRecurringScheduleSession(item));

	if (!rangeStart.isValid() || !rangeEnd.isValid()) return calendarEvents;

	const cancellationLookup = buildCancellationLookup(cancellations);
	const realSessionsByScheduleKey = new Map<string, RealSessionWindow[]>();
	// Group remaining real sessions (without recurringScheduleId) by composite key for time-based matching.
	const realSessionsByKey = new Map<string, RealSessionWindow[]>();
	calendarEvents.forEach((calendarEvent, eventIndex) => {
		if (isDiscretionaryEvent(calendarEvent)) return;
		const event = calendarEvent;
			if (event.recurringScheduleId) {
				const key = `${event.recurringScheduleId}|${toDateKey(event.date)}`;
				const windows = realSessionsByScheduleKey.get(key) ?? [];
				windows.push({ startMinutes: timeToMinutes(event.startTime), endMinutes: timeToMinutes(event.endTime), eventIndex, recurringScheduleTimeSlotId: event.recurringScheduleTimeSlotId, used: false });
				realSessionsByScheduleKey.set(key, windows);
			} else {
				const key = calendarEventKey(event);
				const windows = realSessionsByKey.get(key) ?? [];
				windows.push({ startMinutes: timeToMinutes(event.startTime), endMinutes: timeToMinutes(event.endTime), eventIndex, recurringScheduleTimeSlotId: event.recurringScheduleTimeSlotId, used: false });
				realSessionsByKey.set(key, windows);
			}
	});
	const recurringScheduleSessions = items.filter(isRecurringScheduleSession);
	const expandedRecurringScheduleSessions = recurringScheduleSessions.flatMap((recurringScheduleSession) => {
		const scheduleSessionStart = dayjs(recurringScheduleSession.startDate);
		const scheduleSessionEnd = recurringScheduleSession.endDate ? dayjs(recurringScheduleSession.endDate) : rangeEnd;

		if (!scheduleSessionStart.isValid() || !scheduleSessionEnd.isValid()) return [];

		const firstDate = (scheduleSessionStart.isAfter(rangeStart, 'day') ? scheduleSessionStart : rangeStart).startOf('day');
		const lastDate = (scheduleSessionEnd.isBefore(rangeEnd, 'day') ? scheduleSessionEnd : rangeEnd).startOf('day');
		const events: EventDataProperties[] = [];

		for (let date = firstDate; !date.isAfter(lastDate, 'day'); date = date.add(1, 'day')) {
			if (!shouldCreateRecurringScheduleSessionForDate(recurringScheduleSession, date)) continue;

			const occurrence = applyCancellationToOccurrence(buildRecurringScheduleSessionEvent(recurringScheduleSession, date), cancellationLookup);
			const scheduleKey = `${recurringScheduleSession.id}|${date.format('YYYY-MM-DD')}`;
			const directCandidates = realSessionsByScheduleKey.get(scheduleKey);
			if (directCandidates) {
				const directMatchIndex = findMatchingRealSessionIndex(directCandidates, recurringScheduleSession.startTimeMinutes, recurringScheduleSession.endTimeMinutes, recurringScheduleSession.recurringScheduleTimeSlotId);
				if (directMatchIndex !== -1) {
					const match = directCandidates[directMatchIndex];
					match.used = true;
					if (match.eventIndex !== undefined) calendarEvents[match.eventIndex] = transferOccurrenceState(calendarEvents[match.eventIndex], occurrence);
					continue;
				}
			}

			// Fallback match: manual sessions (no recurringScheduleId) matched by provider/client/sessionType/date + time overlap.
			// A cancelled ghost must NOT absorb a manual session — keep both the cancellation and the manual session visible.
			if (!occurrence.isCancelled) {
				const key = recurringScheduleSessionKey(recurringScheduleSession, date);
				const candidates = realSessionsByKey.get(key);

				if (candidates) {
					const matchIndex = findMatchingRealSessionIndex(candidates, recurringScheduleSession.startTimeMinutes, recurringScheduleSession.endTimeMinutes, recurringScheduleSession.recurringScheduleTimeSlotId);

					if (matchIndex !== -1) {
						const match = candidates[matchIndex];
						match.used = true;
						if (match.eventIndex !== undefined) calendarEvents[match.eventIndex] = transferOccurrenceState(calendarEvents[match.eventIndex], occurrence);
						continue;
					}
				}
			}

			events.push(occurrence);
		}

		return events;
	});

	return [...calendarEvents, ...expandedRecurringScheduleSessions];
};

// Single source of truth for "does this date fall within this cancellation's inclusive date
// range". Used by both the ghost dashed-border overlay (overlayCancellationsOnEvents) and the
// day-strip pending/approved indicator (sessionHeader), so the two indicators can never disagree
// about which days are covered by a given cancellation/request. Mirrors the day-strip's original
// Date-object comparison (local midnight of `date` inclusive through local end-of-day of
// rangeEnd). Bare 'YYYY-MM-DD' strings are routed through dayjs first since native `Date`
// parsing treats date-only strings as UTC (unlike date-time strings, which it treats as local).
export const isDateWithinCancellationRange = (
	date: string | Date,
	rangeStart: string,
	rangeEnd: string,
): boolean => {
	const localDate = typeof date === 'string' ? dayjs(date).toDate() : date;
	const cellDate = new Date(localDate.getFullYear(), localDate.getMonth(), localDate.getDate());

	const start = /^\d{4}-\d{2}-\d{2}$/.test(rangeStart) ? dayjs(rangeStart).toDate() : new Date(rangeStart);
	const end = /^\d{4}-\d{2}-\d{2}$/.test(rangeEnd) ? dayjs(rangeEnd).toDate() : new Date(rangeEnd);
	start.setHours(0, 0, 0, 0);
	end.setHours(23, 59, 59, 999);

	return cellDate >= start && cellDate <= end;
};

// Overlay cancellations onto expanded ghost events:
// - Applied/Approved → mark ghost as cancelled (struck-through, stays visible with reason)
// - Pending → mark with hasPendingCancellation (dashed border hint, still visible)
// Matching:
// - Single-session: recurringScheduleId + date within range
// - Extended cancellation: providerId + optional clientId + date within range
export const overlayCancellationsOnEvents = (
	events: CalendarEventItem[],
	cancellations: ScheduleCancellationViewModel[],
): CalendarEventItem[] => {
	if (!cancellations.length) return events;

	// Single-session cancellations keyed by recurringScheduleId
	const bySchedule = new Map<string, { endDate: string; groupId: string | null; id: string; notes: string | null; reasonName: string | null; recurringScheduleTimeSlotId: string | null; startDate: string; status: CancellationStatus }[]>();
	// Extended cancellations (no recurringScheduleId) — matched by provider + client + date
	const extendedCancellations: ScheduleCancellationViewModel[] = [];

	cancellations.forEach((c) => {
		if (c.recurringScheduleId) {
			const list = bySchedule.get(c.recurringScheduleId) ?? [];
			list.push({ endDate: c.endDate, groupId: c.groupId, id: c.id, notes: c.notes, reasonName: c.reasonName, recurringScheduleTimeSlotId: c.recurringScheduleTimeSlotId ?? null, startDate: c.startDate, status: c.status });
			bySchedule.set(c.recurringScheduleId, list);
		} else {
			extendedCancellations.push(c);
		}
	});

	const result: CalendarEventItem[] = [];

	for (const event of events) {
		if (isDiscretionaryEvent(event) || !event.isRecurringScheduleSession) {
			result.push(event);
			continue;
		}

		let isCancelled = false;
		let isCancelledApproved = false;
		let isPending = false;
		let reasonName: string | null = '';
		let matchedNotes: string | null = null;
		let matchedCancellationId: string | null = null;
		let matchedCancellationGroupId: string | null = null;

		// Check single-session cancellations
		if (event.recurringScheduleId) {
			const schedCancellations = bySchedule.get(event.recurringScheduleId);
			if (schedCancellations) {
				for (const c of schedCancellations) {
					if (
						(!c.recurringScheduleTimeSlotId || c.recurringScheduleTimeSlotId === event.recurringScheduleTimeSlotId) &&
						isDateWithinCancellationRange(event.date, c.startDate, c.endDate)
					) {
						if (c.status === CancellationStatus.Applied || c.status === CancellationStatus.Approved) {
							isCancelled = true;
							if (c.status === CancellationStatus.Approved) isCancelledApproved = true;
							reasonName = c.reasonName;
							matchedNotes = c.notes;
							matchedCancellationId = c.id;
							matchedCancellationGroupId = c.groupId;
							break;
						}
						if (c.status === CancellationStatus.Pending) {
							isPending = true;
						}
					}
				}
			}
		}

		// Check extended cancellations (provider + optional client + date range)
		if (!isCancelled) {
			for (const c of extendedCancellations) {
				if (
					c.providerId === event.user.id &&
					(!c.clientId || c.clientId === event.client.id) &&
					isDateWithinCancellationRange(event.date, c.startDate, c.endDate)
				) {
					if (c.status === CancellationStatus.Applied || c.status === CancellationStatus.Approved) {
						isCancelled = true;
						if (c.status === CancellationStatus.Approved) isCancelledApproved = true;
						reasonName = c.reasonName;
						matchedNotes = c.notes;
						matchedCancellationId = c.id;
						matchedCancellationGroupId = c.groupId;
						break;
					}
					if (c.status === CancellationStatus.Pending) {
						isPending = true;
					}
				}
			}
		}

		// Applied/Approved → keep visible but mark as cancelled (struck-through)
		if (isCancelled) {
			result.push({ ...event, isCancelled: true, isCancelledApproved, cancellationReasonName: reasonName, cancellationNotes: matchedNotes, cancellationId: matchedCancellationId, cancellationGroupId: matchedCancellationGroupId });
			continue;
		}

		// Pending → mark with visual hint
		if (isPending) {
			result.push({ ...event, hasPendingCancellation: true });
		} else {
			result.push(event);
		}
	}

	return result;
};

// Converts 'MM-DD-YYYY' to 'YYYY-MM-DD' for API calls
const reformatDateForApi = (mmDdYyyy: string): string => {
	const parts = mmDdYyyy.split('-');
	if (parts.length === 3 && parts[2].length === 4) {
		return `${parts[2]}-${parts[0]}-${parts[1]}`;
	}
	return mmDdYyyy;
};

export const createEvent = createAsyncThunk('/calendar/create-event', async (data: CreateEventProperties, thunkAPI) => {
	const response = await API.ClientAPI.Calendar.createEvent(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as EventDataProperties;
});

export const adminCreateEvent = createAsyncThunk(
	'/calendar/admin/create-event',
	async (data: AdminCreateEventProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.adminCreateEvent(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response as EventDataProperties;
	},
);

export const copyEvents = createAsyncThunk('/calendar/copy-events', async (data: CopyEventsProperties, thunkAPI) => {
	const response = await API.ClientAPI.Calendar.copyEvents(data);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response;
});

export const getEvents = createAsyncThunk(
	'/calendar/get-events',
	async ({ days, clientId, userId, includeBTEvents, includeBCBAEvents }: GetEventsProperties, thunkAPI) => {
		const range = findDateRange(days);
		let response: any = null;

		if (!clientId && !userId) {
			response = await API.ClientAPI.Calendar.getAllEvents({
				...range,
				includeBTEvents,
				includeBCBAEvents,
			});
		}

		if (!clientId && userId) {
			response = await API.ClientAPI.Calendar.getUserEvents({
				...range,
				userId,
			});
		}

		if (clientId && !userId) {
			response = await API.ClientAPI.Calendar.getClientEvents({
				...range,
				clientId,
			});
		}

		if (clientId && userId) {
			const clientEvents = await API.ClientAPI.Calendar.getClientEvents({
				...range,
				clientId,
			});

			if (clientEvents.error) {
				return thunkAPI.rejectWithValue(clientEvents.error);
			}
			response = clientEvents.filter((x: EventDataProperties | RecurringScheduleSessionProperties) =>
				isRecurringScheduleSession(x) ? x.providerId === userId : x.user.id === userId);
		}

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		let cancellations: ScheduleCancellationViewModel[] = [];

		try {
			const apiStartDate = reformatDateForApi(range.startDate);
			const apiEndDate = reformatDateForApi(range.endDate);
			const providerIds = Array.from(new Set((response as unknown[]).flatMap((item) => {
				if (isRecurringScheduleSession(item)) return item.providerId ? [item.providerId] : [];
				if ((item as { type?: string })?.type === 'DiscretionaryHoursLog') return [];
				const event = item as EventDataProperties;
				return event.user?.id ? [event.user.id] : [];
			})));

			if (providerIds.length > 0) {
				const cancellationsResponse = await API.ClientAPI.ScheduleCancellation.getCancellationsForCalendar({
					providerIds,
					startDate: apiStartDate,
					endDate: apiEndDate,
					...(clientId ? { clientIds: [clientId] } : {}),
				});

				if (!cancellationsResponse.error && Array.isArray(cancellationsResponse)) {
					cancellations = cancellationsResponse as ScheduleCancellationViewModel[];
				}
			}
		} catch {
			cancellations = [];
		}

		return expandRecurringScheduleSessions(response, range.startDate, range.endDate, cancellations);
	},
);

export const createDiscretionaryHours = createAsyncThunk(
	'/calendar/create-discretionary-hours',
	async (data: CreateDiscretionaryHoursProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.createDiscretionaryHours(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const updateDiscretionaryHours = createAsyncThunk(
	'/calendar/update-discretionary-hours',
	async ({ data, id }: { data: CreateDiscretionaryHoursProperties; id: string }, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.updateDiscretionaryHours(id, data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const deleteDiscretionaryHours = createAsyncThunk(
	'/calendar/delete-discretionary-hours',
	async (id: string, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.deleteDiscretionaryHours(id);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const getAllEventsForOneDay = createAsyncThunk(
	'/calendar/all-events-for-one-day',
	async ({ includeBTEvents, date }: { date: string; includeBTEvents?: boolean }, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.getAllEvents({
			startDate: date,
			endDate: date,
			includeBTEvents,
		});

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		// This day-events list feeds the delete-events flow (client picker), not the calendar render,
		// so recurring-schedule rules/ghosts must be excluded to keep that flow identical to before.
		return (response as unknown[]).filter((x): x is EventDataProperties => !isRecurringScheduleSession(x));
	},
);

export const deleteEvent = createAsyncThunk('/calendar/delete-event', async (eventId: string, thunkAPI) => {
	const response = await API.ClientAPI.Calendar.deleteEvent(eventId);

	if (response.error) {
		return thunkAPI.rejectWithValue(response.error);
	}

	return response as EventDataProperties[];
});

export const deleteUpcomingEvent = createAsyncThunk(
	'/calendar/delete-upcoming-event',
	async (data: DeleteUpcomingEventsProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.deleteUpcomingEvents(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const deleteEvents = createAsyncThunk(
	'/calendar/delete-events',
	async (data: DeleteEventsProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.deleteEvents(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

export const deleteUserEvent = createAsyncThunk(
	'/calendar/delete-user-event',
	async (data: DeleteUserEventProperties, thunkAPI) => {
		const response = await API.ClientAPI.Calendar.deleteUserEvent(data);

		if (response.error) {
			return thunkAPI.rejectWithValue(response.error);
		}

		return response;
	},
);

const CalendarSlice = createSlice({
	name: 'calendar',
	initialState: InitialCalendarState,
	reducers: {
		clearEvents(state) {
			state.events = InitialCalendarState.events;
			state.loading = false;
			state.activeGetEventsRequestId = null;
		},
	},
	extraReducers: (builder) => {
		// deleteUserEvent
		builder.addCase(deleteUserEvent.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(deleteUserEvent.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(deleteUserEvent.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// get events
		builder.addCase(getEvents.pending, (state, action) => {
			state.loading = true;
			state.activeGetEventsRequestId = action.meta.requestId;
		});
		builder.addCase(getEvents.fulfilled, (state, action) => {
			if (action.meta.requestId !== state.activeGetEventsRequestId) return;

			state.loading = false;
			state.events = action.payload;
		});
		builder.addCase(getEvents.rejected, (state, action) => {
			if (action.meta.requestId !== state.activeGetEventsRequestId) return;

			state.loading = false;
			state.error = action.error as ErrorProperties;
		});
		// create event
		builder.addCase(createEvent.pending, (state) => {
			state.isCreating = true;
		});
		builder.addCase(createEvent.fulfilled, (state, action) => {
			state.isCreating = false;
			state.event = action.payload;
		});
		builder.addCase(createEvent.rejected, (state, action) => {
			state.isCreating = false;
			state.error = action.error as ErrorProperties;
		});

		// adminCreateEvent
		builder.addCase(adminCreateEvent.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(adminCreateEvent.fulfilled, (state, action) => {
			state.loading = false;
			state.event = action.payload;
		});
		builder.addCase(adminCreateEvent.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});

		// copy events
		builder.addCase(copyEvents.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(copyEvents.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(copyEvents.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});

		// delete event
		builder.addCase(deleteEvent.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteEvent.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteEvent.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});

		// delete events
		builder.addCase(deleteEvents.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteEvents.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteEvents.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});

		// update discretionary hours
		builder.addCase(updateDiscretionaryHours.pending, (state) => {
			state.loading = true;
		});
		builder.addCase(updateDiscretionaryHours.fulfilled, (state) => {
			state.loading = false;
		});
		builder.addCase(updateDiscretionaryHours.rejected, (state, action) => {
			state.loading = false;
			state.error = action.error as ErrorProperties;
		});

		// delete discretionary hours
		builder.addCase(deleteDiscretionaryHours.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteDiscretionaryHours.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteDiscretionaryHours.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});

		// delete upcoming events
		builder.addCase(deleteUpcomingEvent.pending, (state) => {
			state.isDeleting = true;
		});
		builder.addCase(deleteUpcomingEvent.fulfilled, (state) => {
			state.isDeleting = false;
		});
		builder.addCase(deleteUpcomingEvent.rejected, (state, action) => {
			state.isDeleting = false;
			state.error = action.error as ErrorProperties;
		});
	},
});

export const { clearEvents } = CalendarSlice.actions;
export default CalendarSlice.reducer;
